import { useQuery } from '@apollo/client';
import { usePaginationSearch, useRouter } from '@application/utils';
import { graphql } from '@gql/gql';
import { LitterScalarFieldEnum } from '@gql/graphql';
import { useCallback, useMemo, useState } from 'react';
import { PAGINATED_SIZE } from '../constants';
import { useSearch } from '../useSearch';
import { useLitterOrderBuilder } from './useLitterOrderBuilder';
import { useLitterWhereBuilder } from './useLitterWhereBuilder';

export const LITTER_LIST_GET_LITTERS = graphql(`
  query LitterListGetLitters(
    $take: Int
    $skip: Int
    $where: LitterWhereInput
    $orderBy: [LitterOrderByWithRelationAndSearchRelevanceInput!]
    $distinct: [LitterScalarFieldEnum!]
    $litterCardInfoImgsTake: Int!
    $litterCardInfoTranslationsWhere: LitterTranslationWhereInput!
    $litterCardInfoBreedTranslationsWhere: DogBreedTranslationWhereInput!
  ) {
    findManyLitter(
      skip: $skip
      take: $take
      where: $where
      orderBy: $orderBy
      distinct: $distinct
    ) {
      ...LitterCardInfo
    }
    aggregateLitter(where: $where) {
      _count {
        _all
      }
    }
  }
`);

export const useGetLitters = () => {
  const { language } = useRouter();
  const { valuesFromSearch } = useSearch();
  const { litterWhereBuilder } = useLitterWhereBuilder();
  const { currentPage } = usePaginationSearch();
  const [littersFetchMoreLoading, setLittersFetchMoreLoading] = useState(false);
  const { litterOrderBuilder } = useLitterOrderBuilder();

  const variables = useMemo(() => {
    return {
      take: PAGINATED_SIZE * (currentPage + 1),
      skip: 0,
      where: litterWhereBuilder(valuesFromSearch),
      orderBy: litterOrderBuilder(),
      litterCardInfoImgsTake: 1,
      litterCardInfoTranslationsWhere: { language: { equals: language } },
      litterCardInfoBreedTranslationsWhere: { language: { equals: language } },
      distinct: [LitterScalarFieldEnum.id],
    };
  }, [currentPage, language, litterOrderBuilder, litterWhereBuilder, valuesFromSearch]);

  const { data, error, loading, fetchMore, refetch } = useQuery(LITTER_LIST_GET_LITTERS, {
    variables,
  });

  const littersFetchMore = useCallback(
    async (...args: Parameters<typeof fetchMore>) => {
      setLittersFetchMoreLoading(true);
      try {
        await fetchMore(...args);
      } finally {
        setLittersFetchMoreLoading(false);
      }
    },
    [fetchMore],
  );

  return {
    litters: data?.findManyLitter ?? [],
    // eslint-disable-next-line no-underscore-dangle
    littersCount: data?.aggregateLitter?._count?._all ?? 0,
    littersError: error,
    littersLoading: loading,
    littersVariables: variables,
    littersFetchMoreLoading,
    littersRefetch: refetch,
    littersFetchMore,
  };
};
